import { useNavigate } from '@tanstack/react-router'
import { CantileverButton } from 'components/CantileverButton'
import { CreateCaseModal } from 'features/cases'
import { generateEntrySlug } from 'features/rolodex'
import { useResolvedChart, useScatterplotContext } from 'features/timeSeries'
import {
  serializeDateConfig,
  serializeSourceConfigs,
} from 'features/timeSeries/utils/chartSerialization'
import { formatTimeAgo } from 'features/timeSeries/utils/timeUtils'
import { ArrowLeft, Sparkles } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { RichTextContent, RolodexEntryType } from 'types'

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  gap: 0.5rem;
  z-index: 10;
`

const SavingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  justify-content: center;
  gap: 0.125rem;
`

const SavingTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  white-space: nowrap;
  cursor: default;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  height: 2rem;
  padding: 0 0.5rem;
`

const UnsavedChangesText = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.6rem;
`

const LastSavedText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.6rem;
`

const RightSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`

const HeaderButton = styled(CantileverButton)`
  flex-basis: content;
`

const RevertButton = styled(HeaderButton)`
  border-radius: 0.25rem 0 0 0.25rem;
`

const SaveButton = styled(HeaderButton)`
  border-radius: 0 0.25rem 0.25rem 0;
`

interface DashboardHeaderProps {
  toggleChatbot?: () => void
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  toggleChatbot,
}) => {
  const {
    saveChart,
    revertChanges,
    hasUnsavedChanges,
    lastSavedAt,
    siteID,
    organizationID,
    chartID,
  } = useScatterplotContext()

  const navigate = useNavigate()
  const [isCaseModalOpen, setIsCaseModalOpen] = useState(false)
  const { chart, isLoading: isLoadingChart } = useResolvedChart(chartID)
  const [timeAgoText, setTimeAgoText] = useState(() =>
    formatTimeAgo(lastSavedAt)
  )

  const { chartCopyContent, chartCopyEntry } = useMemo(() => {
    if (!chart) {
      return { chartCopyContent: undefined, chartCopyEntry: undefined }
    }

    const newChartID = generateEntrySlug(RolodexEntryType.CHART)
    const chartName = chart.name || 'Untitled Chart'

    const content = `
    <span data-lexical-slash-command="true" data-entry-type="chart" data-entry-id="${newChartID}" data-entry-name="${chartName}" data-metadata="{&quot;siteId&quot;:&quot;${siteID}&quot;,&quot;orgId&quot;:&quot;${organizationID}&quot;}"></span>
    ` as RichTextContent

    const entry = {
      id: newChartID,
      type: RolodexEntryType.CHART,
      data: {
        name: chart.name,
        description: chart.description,
        source_configs: serializeSourceConfigs(chart.sourceConfigs),
        y_axis_mode: chart.yAxisMode,
        date_config: serializeDateConfig(chart.dateConfig),
        ...(chart.dataProcessingConfigs && {
          data_processing_configs: chart.dataProcessingConfigs,
        }),
      },
      tags: {
        site: siteID,
        organization: organizationID,
      },
    }

    return { chartCopyContent: content, chartCopyEntry: entry }
  }, [chart, siteID, organizationID])

  useEffect(() => {
    setTimeAgoText(formatTimeAgo(lastSavedAt))

    const interval = setInterval(() => {
      setTimeAgoText(formatTimeAgo(lastSavedAt))
    }, 60000)

    return () => clearInterval(interval)
  }, [lastSavedAt])

  return (
    <>
      <HeaderContainer>
        <HeaderButton iconOnly ghost onClick={() => navigate({ to: '..' })}>
          <ArrowLeft width={'1.25rem'} height={'1.25rem'} />
        </HeaderButton>
        <SavingContainer>
          {hasUnsavedChanges ? (
            <>
              <RevertButton
                onClick={revertChanges}
                disabled={!hasUnsavedChanges}>
                Revert
              </RevertButton>
              <SavingTextContainer>
                <UnsavedChangesText>
                  Dashboard has unsaved changes
                </UnsavedChangesText>
                <LastSavedText>{timeAgoText}</LastSavedText>
              </SavingTextContainer>
              <SaveButton onClick={saveChart} disabled={!hasUnsavedChanges}>
                Save
              </SaveButton>
            </>
          ) : (
            <LastSavedText>{timeAgoText}</LastSavedText>
          )}
        </SavingContainer>
        <RightSection>
          <HeaderButton
            onClick={() => setIsCaseModalOpen(true)}
            disabled={isLoadingChart}>
            Create Case
          </HeaderButton>
          {toggleChatbot && (
            <HeaderButton iconOnly primary onClick={toggleChatbot}>
              <Sparkles width={'1.25rem'} height={'1.25rem'} />
            </HeaderButton>
          )}
        </RightSection>
      </HeaderContainer>
      <CreateCaseModal
        isOpen={isCaseModalOpen}
        onClose={() => setIsCaseModalOpen(false)}
        siteID={siteID}
        organizationID={organizationID}
        initialCaseContent={chartCopyContent}
        additionalEntriesToPatch={chartCopyEntry ? [chartCopyEntry] : undefined}
      />
    </>
  )
}
